import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

const initialState = {
  machines: [],
  currentMachine: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchMachines = createAsyncThunk(
  'machines/fetchMachines',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/machines');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Machines laden mislukt');
    }
  }
);

export const fetchMachineById = createAsyncThunk(
  'machines/fetchMachineById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/machines/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Machine laden mislukt');
    }
  }
);

export const updateMachineStatus = createAsyncThunk(
  'machines/updateStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/machines/${id}/status`, { status });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Status update mislukt');
    }
  }
);

export const createMachine = createAsyncThunk(
  'machines/create',
  async (machineData, { rejectWithValue }) => {
    try {
      const response = await api.post('/machines', machineData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Machine aanmaken mislukt');
    }
  }
);

export const setDefaultMachine = createAsyncThunk(
  'machines/setDefault',
  async (machineId, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/machines/${machineId}/set-default`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Standaard machine instellen mislukt');
    }
  }
);

export const updateMachine = createAsyncThunk(
  'machines/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/machines/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Machine bijwerken mislukt');
    }
  }
);

export const syncMachineJobs = createAsyncThunk(
  'machines/syncJobs',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/machines/sync-jobs');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Jobs synchroniseren mislukt');
    }
  }
);

const machinesSlice = createSlice({
  name: 'machines',
  initialState,
  reducers: {
    clearCurrentMachine: (state) => {
      state.currentMachine = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch machines
      .addCase(fetchMachines.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMachines.fulfilled, (state, action) => {
        state.loading = false;
        // Handle both array and object response
        state.machines = Array.isArray(action.payload)
          ? action.payload
          : action.payload.machines || [];
      })
      .addCase(fetchMachines.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch single machine
      .addCase(fetchMachineById.fulfilled, (state, action) => {
        state.currentMachine = action.payload;
      })
      // Update status
      .addCase(updateMachineStatus.fulfilled, (state, action) => {
        const index = state.machines.findIndex((m) => m._id === action.payload._id);
        if (index !== -1) {
          state.machines[index] = action.payload;
        }
      })
      // Create machine
      .addCase(createMachine.fulfilled, (state, action) => {
        state.machines.push(action.payload);
      })
      // Set default machine
      .addCase(setDefaultMachine.fulfilled, (state, action) => {
        const defaultMachine = action.payload.machine;
        // Update all machines - only the new default should be isDefault: true
        state.machines = state.machines.map(m => ({
          ...m,
          isDefault: m._id === defaultMachine._id
        }));
      })
      // Update machine
      .addCase(updateMachine.fulfilled, (state, action) => {
        const index = state.machines.findIndex((m) => m._id === action.payload._id);
        if (index !== -1) {
          state.machines[index] = action.payload;
        }
        if (state.currentMachine?._id === action.payload._id) {
          state.currentMachine = action.payload;
        }
      });
  },
});

export const { clearCurrentMachine } = machinesSlice.actions;

// Alias for fetchMachineById
export const fetchMachine = fetchMachineById;

export default machinesSlice.reducer;
